import React, { useEffect, useState } from 'react';
import Sidebar from '../Components/Sidebar';
import MobileNav from '../Components/MobileNav';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria',
  'Bangladesh', 'Belgium', 'Brazil', 'Canada', 'Chile', 'China', 'Colombia',
  'Czech Republic', 'Denmark', 'Egypt', 'Finland', 'France', 'Germany', 'Ghana',
  'Greece', 'Hungary', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Japan', 'Kenya', 'Malaysia', 'Mexico', 'Morocco',
  'Netherlands', 'New Zealand', 'Nigeria', 'Norway', 'Pakistan', 'Philippines',
  'Poland', 'Portugal', 'Romania', 'Russia', 'Saudi Arabia', 'South Africa',
  'South Korea', 'Spain', 'Sweden', 'Switzerland', 'Tanzania', 'Thailand', 
  'Turkey', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Vietnam', 'Zimbabwe'
];

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const colors = {
    success: '#22c55e',
    error: '#ef4444',
    info: '#38bdf8',
    warning: '#f59e0b'
  };

  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: colors[type] || colors.info,
      color: '#000',
      padding: '12px 24px',
      borderRadius: '8px',
      fontWeight: 'bold',
      zIndex: 9999,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      maxWidth: '90vw',
      wordBreak: 'break-word',
    }}>
      {message}
    </div>
  );
};

export default function Settings() {
  const { t, language, setLanguage } = useLanguage();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Theme state
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  
  // Help modal state
  const [activeHelpCard, setActiveHelpCard] = useState(null);

  // Toast state
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'info') => setToast({ message, type });
  const closeToast = () => setToast(null);

  const [accountData, setAccountData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    dob: '',
    age: ''
  });

  const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪' }
];
  useEffect(() => {
    loadUserData();
    document.documentElement.setAttribute('data-theme', theme);
  }, []);

  const loadUserData = async () => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return;

    setUser(authUser);

    const { data: personal, error } = await supabase
      .from('personal')
      .select('*')
      .eq('user_id', authUser.id)
      .maybeSingle();

    if (personal) {
      let calculatedAge = personal.age || '';
      if (personal.dob && !personal.age) {
        const birthDate = new Date(personal.dob);
        const today = new Date();
        calculatedAge = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      }

      setAccountData({
        fullName: personal.name || '',
        email: authUser.email,
        phone: personal.phone || '',
        country: personal.country || '',
        dob: personal.dob || '',
        age: calculatedAge
      });
    } else {
      setAccountData(prev => ({
        ...prev,
        email: authUser.email
      }));
    }
  };

  // Theme toggle
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    showToast(`Switched to ${newTheme} mode`, 'success');
  };

  const handleSave = async () => {
    if (!user) {
      showToast('No user found', 'error');
      return;
    }

    setSaving(true);

    const { error } = await supabase
      .from('personal')
      .upsert({
        user_id: user.id,
        name: accountData.fullName,
        phone: accountData.phone,
        country: accountData.country,
        dob: accountData.dob,
        age: parseInt(accountData.age) || 0
      }, {
        onConflict: 'user_id'
      });

    if (!error) {
      let newAge = '';
      if (accountData.dob) {
        const birthDate = new Date(accountData.dob);
        const today = new Date();
        newAge = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
      }

      setAccountData(prev => ({ ...prev, age: newAge }));
      setIsEditing(false);
      showToast('✓ Account information updated!', 'success');
    } else {
      showToast('Error saving changes: ' + error.message, 'error');
    }

    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!user?.email) {
      showToast('No email found', 'error');
      return;
    }

    showToast('Sending reset email...', 'info');

    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: window.location.origin + '/reset-password',
    });

    if (error) {
      showToast('Error: ' + error.message, 'error');
    } else {
      showToast('✓ Reset email sent! Check your inbox.', 'success');
    }
  };

  const handleLogout = async () => {
    const confirmed = window.confirm('Are you sure you want to logout?');
    if (!confirmed) return;

    setLoggingOut(true);
    await supabase.auth.signOut();
    showToast('✓ Logged out successfully!', 'success');
    setTimeout(() => window.location.href = '/', 1000);
  };

  const handleDeleteAccount = async () => {
  const confirmed = window.confirm(
    '⚠️ WARNING: This will permanently delete your account and all associated data.\n\n' +
    'This includes:\n' +
    '- Your profile and game statistics\n' +
    '- Your followers and following relationships\n' +
    '- Your clan membership\n' +
    '- All personal information\n\n' +
    'This action cannot be undone. Are you sure?'
  );

  if (!confirmed) return;

  const doubleConfirm = window.prompt('Type "DELETE" to confirm account deletion:');
  if (doubleConfirm !== 'DELETE') {
    showToast('Account deletion cancelled.', 'info');
    return;
  }

  setDeleting(true);
  showToast('Deleting account...', 'warning');

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete account');
    }

    await supabase.auth.signOut();
    showToast('✓ Account deleted permanently.', 'success');
    setTimeout(() => window.location.href = '/', 1500);
  } catch (err) {
    console.error('Delete error:', err);
    showToast('Error deleting account: ' + err.message, 'error');
    setDeleting(false);
  }
};

  const infoRow = (label, value, icon, editable = false, field = null, type = 'text') => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: isMobile ? '12px' : '16px',
      background: 'var(--bg)',
      borderRadius: '12px',
      marginBottom: '12px',
      border: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: isMobile ? '20px' : '24px', marginRight: '16px' }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>{label}</div>
        {isEditing && editable ? (
          field === 'country' ? (
            <select
              value={accountData[field] || ''}
              onChange={e => setAccountData({ ...accountData, [field]: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #38bdf8',
                background: 'var(--input-bg)',
                color: 'var(--text)',
                fontSize: '15px',
              }}
            >
              <option value="">Select Country</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input
              type={type}
              value={accountData[field] || ''}
              onChange={e => setAccountData({ ...accountData, [field]: e.target.value })}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #38bdf8',
                background: 'var(--input-bg)',
                color: 'var(--text)',
                fontSize: '15px',
              }}
            />
          )
        ) : (
          <div style={{ fontSize: '16px', color: 'var(--text)', fontWeight: '500' }}>
            {value || <span style={{ color: '#475569', fontStyle: 'italic' }}>Not set</span>}
          </div>
        )}
      </div>
    </div>
  );

  // Help card component
  const HelpCard = ({ icon, title, description, onClick }) => (
    <div 
      onClick={onClick}
      style={{
        background: 'var(--bg)',
        padding: isMobile ? '20px' : '24px',
        borderRadius: '16px',
        border: '1px solid var(--border)',
        cursor: 'pointer',
        transition: 'all 0.2s',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#38bdf8'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      <div style={{ fontSize: isMobile ? '32px' : '40px', marginBottom: '12px' }}>{icon}</div>
      <h3 style={{ color: '#38bdf8', fontSize: isMobile ? '16px' : '18px', marginBottom: '8px', fontWeight: 'bold' }}>{title}</h3>
      <p style={{ color: '#64748b', fontSize: isMobile ? '13px' : '14px', lineHeight: '1.5', marginBottom: '12px' }}>{description}</p>
      <div style={{ color: '#38bdf8', fontSize: '13px', fontWeight: '600' }}>Click to learn more →</div>
    </div>
  );

  // Help modal content
  const helpContent = {
    started: {
      title: '🎮 Getting Started',
      content: (
        <div>
          <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>Welcome to WWMMCGC! Here's how to set up your profile:</p>
          <ol style={{ paddingLeft: '20px', lineHeight: '1.8', color: '#94a3b8' }}>
            <li>Go to the <strong>Account</strong> tab</li>
            <li>Click the <strong>Edit</strong> button</li>
            <li>Fill in your full name, date of birth, and country</li>
            <li>Add your phone number (optional)</li>
            <li>Click <strong>Save</strong> to update your profile</li>
          </ol>
          <p style={{ marginTop: '16px', color: '#64748b' }}>Your profile helps other soldiers recognize you in the community!</p>
        </div>
      )
    },
    following: {
      title: '👥 Following & Followers',
      content: (
        <div>
          <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>Connect with other Mini Militia players:</p>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8', color: '#94a3b8' }}>
            <li>Visit the <strong>Players</strong> tab to find other soldiers</li>
            <li>Click on any player to view their profile</li>
            <li>Click <strong>Follow</strong> to add them to your network</li>
            <li>See your Following/Followers count on your <strong>Dashboard</strong></li>
            <li>Click the count to view the full list</li>
          </ul>
          <p style={{ marginTop: '16px', color: '#64748b' }}>Build your squad and stay connected!</p>
        </div>
      )
    },
    clans: {
      title: '🛡️ Clans',
      content: (
        <div>
          <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>Join forces with other players:</p>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8', color: '#94a3b8' }}>
            <li>Browse available clans in the <strong>Clan</strong> tab</li>
            <li>Click on a clan to view details</li>
            <li>Scroll down and click <strong>Join</strong> button</li>
            <li>Your clan tag appears on your profile</li>
            <li>Represent your clan in the community!</li>
          </ul>
        </div>
      )
    },
    security: {
      title: '🔒 Account Security',
      content: (
        <div>
          <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>Protect your WWMMCGC account:</p>
          
          <h4 style={{ color: '#38bdf8', marginBottom: '8px' }}>Change Password</h4>
          <p style={{ marginBottom: '16px', color: '#94a3b8' }}>Click "Send Reset Email" in the Account tab. Check your inbox for the reset link.</p>
          
          <h4 style={{ color: '#ef4444', marginBottom: '8px' }}>Delete Account</h4>
          <p style={{ marginBottom: '16px', color: '#94a3b8' }}>Scroll to "Danger Zone" in Account tab. Type "DELETE" to confirm. This is permanent!</p>
          
          <h4 style={{ color: '#22c55e', marginBottom: '8px' }}>Tips</h4>
          <ul style={{ paddingLeft: '20px', lineHeight: '1.8', color: '#94a3b8' }}>
            <li>Use a strong, unique password</li>
            <li>Don't share your login credentials</li>
            <li>Log out on shared devices</li>
          </ul>
        </div>
      )
    }
  };

  const renderAccountContent = () => (
    <div>
      {/* Header with Edit button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
      }}>
        <h2 style={{ color: 'var(--text)', fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold' }}>
          Personal Information
        </h2>
        <button
          onClick={() => isEditing ? handleSave() : setIsEditing(true)}
          disabled={saving}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            background: isEditing ? '#22c55e' : '#38bdf8',
            color: '#000',
            fontWeight: 'bold',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '14px',
          }}
        >
          {saving ? 'Saving...' : (isEditing ? '✓ Save' : '✏️ Edit')}
        </button>
      </div>

      {/* Info Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '12px',
        marginBottom: '24px',
      }}>
        {infoRow('Full Name', accountData.fullName, '👤', true, 'fullName')}
        {infoRow('Age', accountData.age, '🎂', false)}
        {infoRow('Date of Birth', accountData.dob, '📅', true, 'dob', 'date')}
        {infoRow('Country', accountData.country, '🌍', true, 'country')}
        {infoRow('Phone', accountData.phone, '📞', true, 'phone', 'tel')}
        {infoRow('Email', accountData.email, '📧', false)}
      </div>

      {/* Security Section */}
      <div style={{ 
        background: 'var(--bg)', 
        padding: '24px', 
        borderRadius: '16px',
        marginBottom: '24px',
        border: '1px solid var(--border)',
      }}>
        <h3 style={{ color: '#38bdf8', fontSize: '18px', marginBottom: '16px', fontWeight: 'bold' }}>
          Security & Access
        </h3>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={handleChangePassword}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              border: '1px solid #38bdf8',
              borderRadius: '8px',
              color: '#38bdf8',
              cursor: 'pointer',
              fontWeight: '600',
            }}
          >
            🔐 Change Password
          </button>
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            style={{
              padding: '12px 20px',
              background: 'transparent',
              border: '1px solid #f87171',
              borderRadius: '8px',
              color: '#f87171',
              cursor: loggingOut ? 'not-allowed' : 'pointer',
              fontWeight: '600',
            }}
          >
            🚪 {loggingOut ? 'Logging out...' : 'Logout'}
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div style={{ 
        background: 'rgba(239, 68, 68, 0.05)', 
        padding: '24px', 
        borderRadius: '16px',
        border: '1px solid rgba(239, 68, 68, 0.3)',
      }}>
        <h3 style={{ color: '#ef4444', fontSize: '18px', marginBottom: '16px', fontWeight: 'bold' }}>
          Danger Zone
        </h3>
        <button
          onClick={handleDeleteAccount}
          disabled={deleting}
          style={{
            padding: '12px 24px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '8px',
            color: '#ef4444',
            fontWeight: 'bold',
            cursor: deleting ? 'not-allowed' : 'pointer',
          }}
        >
          🗑️ {deleting ? 'Deleting...' : 'Delete Account'}
        </button>
      </div>
    </div>
  );

  const renderPrivacyContent = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', marginBottom: '24px' }}>
        Privacy & Legal
      </h2>

      {/* Privacy Policy Card */}
      <div 
        onClick={() => setActiveHelpCard('privacy')}
        style={{
          background: 'var(--bg)',
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid var(--border)',
          marginBottom: '16px',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#38bdf8'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ color: '#38bdf8', fontSize: '18px', marginBottom: '8px', fontWeight: 'bold' }}>📄 Privacy Policy</h3>
            <p style={{ color: '#64748b', fontSize: '14px' }}>Last Updated: March 27, 2026</p>
          </div>
          <span style={{ color: '#38bdf8', fontSize: '20px' }}>→</span>
        </div>
        <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '12px', lineHeight: '1.5' }}>
          Learn how we collect, use, and protect your personal information.
        </p>
      </div>

      {/* Terms Card */}
      <div 
        onClick={() => setActiveHelpCard('terms')}
        style={{
          background: 'var(--bg)',
          padding: '24px',
          borderRadius: '16px',
          border: '1px solid var(--border)',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = '#38bdf8'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ color: '#8b5cf6', fontSize: '18px', marginBottom: '8px', fontWeight: 'bold' }}>📋 Terms & Conditions</h3>
            <p style={{ color: '#64748b', fontSize: '14px' }}>Last Updated: March 27, 2026</p>
          </div>
          <span style={{ color: '#8b5cf6', fontSize: '20px' }}>→</span>
        </div>
        <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '12px', lineHeight: '1.5' }}>
          Rules and guidelines for using WWMMCGC platform.
        </p>
      </div>
    </div>
  );

  const renderLanguageContent = () => (
  <div>
    <h2 style={{ color: 'var(--text)', fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', marginBottom: '16px' }}>
      {t('language')}
    </h2>
    <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '14px' }}>
      Current language: <strong>{language}</strong> {/* DEBUG: Show current */}
    </p>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {LANGUAGES.map((lang) => (
        <div
          key={lang.code}
          onClick={() => {
            console.log('Clicked language:', lang.code); // DEBUG
            setLanguage(lang.code);
            console.log('After setLanguage called'); // DEBUG
          }}
          style={{
            padding: '16px 20px',
            background: language === lang.code ? 'rgba(56, 189, 248, 0.1)' : 'var(--bg)',
            border: language === lang.code ? '2px solid #38bdf8' : '1px solid var(--border)',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '28px' }}>{lang.flag}</span>
            <div>
              <div style={{ color: 'var(--text)', fontWeight: language === lang.code ? 'bold' : '500', fontSize: '16px' }}>
                {lang.nativeName}
              </div>
              <div style={{ color: '#64748b', fontSize: '13px' }}>{lang.name}</div>
            </div>
          </div>
          {language === lang.code && <span style={{ color: '#38bdf8', fontSize: '20px', fontWeight: 'bold' }}>✓</span>}
        </div>
      ))}
    </div>
  </div>
);

  const renderHelpContent = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold', marginBottom: '24px' }}>
        Help Center
      </h2>

      {/* Help Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
        gap: '16px',
        marginBottom: '32px',
      }}>
        <HelpCard 
          icon="🎮" 
          title="Getting Started" 
          description="Learn how to create your profile and set up your account"
          onClick={() => setActiveHelpCard('started')}
        />
        <HelpCard 
          icon="👥" 
          title="Following & Followers" 
          description="Connect with other soldiers and build your network"
          onClick={() => setActiveHelpCard('following')}
        />
        <HelpCard 
          icon="🛡️" 
          title="Clans" 
          description="Join or find your clan and team up with others"
          onClick={() => setActiveHelpCard('clans')}
        />
        <HelpCard 
          icon="🔒" 
          title="Account Security" 
          description="Protect your account, reset password, or delete account"
          onClick={() => setActiveHelpCard('security')}
        />
      </div>

      {/* FAQ Section */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ color: '#38bdf8', fontSize: '18px', marginBottom: '16px', fontWeight: 'bold' }}>
          Frequently Asked Questions
        </h3>
        
        {[
          { 
            q: 'How do I update my stats?', 
            a: 'You can update your stats in your profile. Probably monthly, manually.' 
          },
          { 
            q: 'Can I change my avatar name?', 
            a: 'Yes. Go to Profile, enter your new avatar name and save.' 
          },
          { 
            q: 'How do I join a clan?', 
            a: 'Browse available clans, click on it and scroll down you\'ll see a join button, click on it.' 
          },
          { 
            q: 'Why are my followers not showing?', 
            a: 'Followers may take a moment to load. Try refreshing the page or check your internet connection. If the issue persists, log out and log back in.' 
          },
          { 
            q: 'Is my data safe?', 
            a: 'Yes, your data is encrypted and stored securely. We never share your personal information with third parties without your consent.' 
          }
        ].map((faq, idx) => (
          <div key={idx} style={{
            background: 'var(--bg)',
            padding: '16px 20px',
            borderRadius: '12px',
            border: '1px solid var(--border)',
            marginBottom: '12px',
          }}>
            <div style={{ fontWeight: 'bold', color: 'var(--text)', marginBottom: '8px', fontSize: '15px' }}>
              Q: {faq.q}
            </div>
            <div style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6' }}>
              A: {faq.a}
            </div>
          </div>
        ))}
      </div>

      {/* Contact Support */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
        padding: '24px',
        borderRadius: '16px',
        border: '1px solid rgba(56, 189, 248, 0.3)',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>💬</div>
        <h3 style={{ color: 'var(--text)', fontSize: '18px', marginBottom: '8px' }}>Still need help?</h3>
        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '16px' }}>
          Our support team is ready to assist you with any issues.
        </p>
        <button
          onClick={() => window.location.href = 'mailto:support@wwmmcg.netlify.app'}
          style={{
            padding: '12px 24px',
            background: '#38bdf8',
            border: 'none',
            borderRadius: '8px',
            color: '#000',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          📧 Contact Support
        </button>
      </div>
    </div>
  );

  // Modal for help cards and privacy/terms
  const renderModal = () => {
    if (!activeHelpCard) return null;

    let content = null;
    let title = '';

    if (activeHelpCard === 'privacy') {
      title = '📄 Privacy Policy';
      content = (
        <div style={{ lineHeight: '1.8', color: '#94a3b8' }}>
          <p style={{ marginBottom: '16px' }}><strong style={{ color: '#fff' }}>Last Updated:</strong> March 27, 2026</p>
          
          <h4 style={{ color: '#38bdf8', marginBottom: '12px' }}>1. Information We Collect</h4>
          <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
            <li>Account data (email, password via Supabase Auth)</li>
            <li>Profile information (name, age, country, phone, avatar)</li>
            <li>Social data (followers, following relationships)</li>
          </ul>

          <h4 style={{ color: '#38bdf8', marginBottom: '12px' }}>2. How We Use Your Information</h4>
          <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
            <li>Create and manage your account</li>
            <li>Display your profile to other players</li>
            <li>Enable social features</li>
          </ul>

          <h4 style={{ color: '#38bdf8', marginBottom: '12px' }}>3. Data Security</h4>
          <p>All data is stored securely using Supabase with encryption.</p>
        </div>
      );
    } else if (activeHelpCard === 'terms') {
      title = '📋 Terms & Conditions';
      content = (
        <div style={{ lineHeight: '1.8', color: '#94a3b8' }}>
          <p style={{ marginBottom: '16px' }}><strong style={{ color: '#fff' }}>Last Updated:</strong> March 27, 2026</p>
          
          <h4 style={{ color: '#8b5cf6', marginBottom: '12px' }}>1. Acceptance of Terms</h4>
          <p style={{ marginBottom: '16px' }}>By using WWMMCGC, you agree to be bound by these Terms.</p>

          <h4 style={{ color: '#8b5cf6', marginBottom: '12px' }}>2. User Accounts</h4>
          <ul style={{ marginBottom: '16px', paddingLeft: '20px' }}>
            <li>You must provide accurate information</li>
            <li>You are responsible for safeguarding your password</li>
            <li>You must be at least 13 years old</li>
          </ul>

          <h4 style={{ color: '#8b5cf6', marginBottom: '12px' }}>3. Acceptable Use</h4>
          <ul style={{ paddingLeft: '20px' }}>
            <li>No offensive, hateful, or inappropriate language</li>
            <li>No impersonation of other players</li>
            <li>No harassment or bullying</li>
          </ul>
        </div>
      );
    } else if (helpContent[activeHelpCard]) {
      title = helpContent[activeHelpCard].title;
      content = helpContent[activeHelpCard].content;
    }

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px',
      }} onClick={() => setActiveHelpCard(null)}>
        <div 
          style={{
            background: 'var(--card-bg)',
            padding: '32px',
            borderRadius: '20px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
            border: '1px solid var(--border)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--text)', fontSize: '20px', fontWeight: 'bold' }}>{title}</h3>
            <button 
              onClick={() => setActiveHelpCard(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#64748b',
                fontSize: '24px',
                cursor: 'pointer',
              }}
            >
              ×
            </button>
          </div>
          {content}
        </div>
      </div>
    );
  };

  // Top navigation tabs with theme toggle
  const renderTopNav = () => {
    const tabs = [
      { id: 'account', label: 'Account', icon: '👤' },
      { id: 'privacy', label: 'Privacy', icon: '🔒' },
      { id: 'language', label: 'Language', icon: '🌐' },
      { id: 'help', label: 'Help', icon: '❓' },
    ];

    return (
      <div style={{
        display: 'flex',
        gap: isMobile ? '8px' : '12px',
        marginBottom: '24px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: isMobile ? '10px 14px' : '12px 20px',
              borderRadius: '10px',
              border: activeTab === tab.id ? '1px solid #38bdf8' : '1px solid var(--border)',
              background: activeTab === tab.id ? 'rgba(56, 189, 248, 0.1)' : 'var(--bg)',
              color: activeTab === tab.id ? '#38bdf8' : '#64748b',
              fontWeight: activeTab === tab.id ? 'bold' : '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: isMobile ? '13px' : '14px',
              transition: 'all 0.2s',
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
        
        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          style={{
            padding: isMobile ? '10px 14px' : '12px 16px',
            borderRadius: '10px',
            border: '1px solid var(--border)',
            background: theme === 'dark' ? 'rgba(251, 191, 36, 0.1)' : 'rgba(56, 189, 248, 0.1)',
            color: theme === 'dark' ? '#fbbf24' : '#38bdf8',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: isMobile ? '13px' : '14px',
            marginLeft: 'auto',
          }}
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          <span style={{ fontSize: '18px' }}>{theme === 'dark' ? '🌙' : '☀️'}</span>
          <span>{theme === 'dark' ? 'Dark' : 'Light'}</span>
        </button>
      </div>
    );
  };

  return (
    <div style={{ 
      display: 'flex', 
      minHeight: '100vh', 
      background: 'var(--bg)',
      color: 'var(--text)',
      flexDirection: isMobile ? 'column' : 'row',
    }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      {renderModal()}

      {!isMobile && <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />}

      <div style={{ 
        flex: 1, 
        marginLeft: isMobile ? '0' : (collapsed ? '80px' : '280px'),
        padding: isMobile ? '16px' : '40px',
        paddingBottom: isMobile ? '100px' : '40px',
        transition: 'margin-left 0.3s ease',
        maxWidth: isMobile ? '100%' : '900px',
      }}>
        <h1 style={{ 
          marginBottom: '24px',
          fontSize: isMobile ? '24px' : '32px',
          fontWeight: 'bold',
        }}>
          {t('settings')}
        </h1>

        {renderTopNav()}

        <div style={{ 
          background: 'var(--card-bg)', 
          borderRadius: '20px',
          padding: isMobile ? '20px' : '32px',
          border: '1px solid var(--border)',
        }}>
          {activeTab === 'account' && renderAccountContent()}
          {activeTab === 'privacy' && renderPrivacyContent()}
          {activeTab === 'language' && renderLanguageContent()}
          {activeTab === 'help' && renderHelpContent()}
        </div>
      </div>

      {isMobile && <MobileNav />}
    </div>
  );
}